/**
 * Bytte av palett og lys i drift.
 *
 * Hvorfor ikke `map.setStyle()`: den ville kastet ut alle custom layers
 * (klokketårn, busskur, kjøretøymarkører, tunnelbuer), terrengkilden og
 * rutekildene, og alt måtte vært bygget opp igjen. I stedet muterer vi
 * `PALETTE`/`ROUTE_COLORS`, bygger laglistene på nytt for å få ut de nye
 * paint-verdiene, og setter dem på lagene som allerede finnes. Hele scenen
 * står, bare fargene skifter.
 */

import { PALETTE } from './style.js';
import { buildBaseLayers } from './layers.js';
import { ROUTE_COLORS, routeLayers, ROUTE_STRUCTURES } from './route.js';
import { PALETTES, DEFAULT_PALETTE, HOTKEYS } from './palettes.js';

const STORAGE_KEY = 'ruterracet.palette';

/** Setter alle paint-verdier fra en laglistemal på lagene som finnes. */
function applyPaint(map, layers) {
  for (const layer of layers) {
    for (const [prop, value] of Object.entries(layer.paint ?? {})) {
      if (map.getLayer(layer.id)) map.setPaintProperty(layer.id, prop, value);
      // addRoutes klonet veilagene til et eget bru-pass; de skal følge med.
      const clone = `route-bridge-cover-${layer.id}`;
      if (map.getLayer(clone)) map.setPaintProperty(clone, prop, value);
    }
  }
}

/**
 * @param {import('maplibre-gl').Map} map
 * @param {string} key nøkkel i PALETTES
 */
export function applyPalette(map, key) {
  const theme = PALETTES[key];
  if (!theme) return;

  Object.assign(PALETTE, theme.palette);
  for (const mode of ['bus', 'car']) Object.assign(ROUTE_COLORS[mode], theme.routes[mode]);

  // Bakgrunnslaget bygges i buildStyle(), ikke i buildBaseLayers().
  if (map.getLayer('background')) {
    map.setPaintProperty('background', 'background-color', PALETTE.background);
  }
  applyPaint(map, buildBaseLayers());
  for (const structure of ROUTE_STRUCTURES) {
    for (const mode of ['car', 'bus']) applyPaint(map, routeLayers(mode, structure));
  }

  map.setLight(theme.light);
  map.setSky(theme.sky);
  localStorage.setItem(STORAGE_KEY, key);
}

/** Sist valgte palett, eller standarden. */
export function storedPalette() {
  const key = localStorage.getItem(STORAGE_KEY);
  return key && PALETTES[key] ? key : DEFAULT_PALETTE;
}

/**
 * Kobler nedtrekksmeny + lysskyvere.
 * Lyset settes direkte på kartet, ikke i paletten, så man kan finstille
 * en palett uten å redigere fil – og se effekten med en gang.
 */
export function wireThemeControls(map, root) {
  const select = root.querySelector('#palette-select');
  for (const [key, theme] of Object.entries(PALETTES)) {
    select.append(new Option(theme.label, key));
  }

  const sliders = {
    azimuth: root.querySelector('#light-azimuth'),
    polar: root.querySelector('#light-polar'),
    intensity: root.querySelector('#light-intensity'),
  };

  function syncSliders(light) {
    sliders.azimuth.value = light.position[1];
    sliders.polar.value = light.position[2];
    sliders.intensity.value = Math.round(light.intensity * 100);
    showValues();
  }

  function showValues() {
    root.querySelector('#light-azimuth-val').textContent = `${sliders.azimuth.value}°`;
    root.querySelector('#light-polar-val').textContent = `${sliders.polar.value}°`;
    root.querySelector('#light-intensity-val').textContent = `${sliders.intensity.value}%`;
  }

  function pushLight() {
    const base = PALETTES[select.value].light;
    map.setLight({
      ...base,
      position: [base.position[0], Number(sliders.azimuth.value), Number(sliders.polar.value)],
      intensity: Number(sliders.intensity.value) / 100,
    });
    showValues();
  }

  select.addEventListener('change', () => {
    applyPalette(map, select.value);
    syncSliders(PALETTES[select.value].light);
  });
  for (const el of Object.values(sliders)) el.addEventListener('input', pushLight);

  // Tastene 1-9 bytter palett med UENDRET kamera. Det er hele poenget:
  // skal man sammenligne paletter, må utsnittet være identisk - ellers
  // vurderer man to bilder samtidig som man leter seg tilbake til samme sted.
  window.addEventListener('keydown', (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const target = e.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) return;
    const next = HOTKEYS[e.key];
    if (!next) return;
    select.value = next;
    applyPalette(map, next);
    syncSliders(PALETTES[next].light);
  });

  const key = storedPalette();
  select.value = key;
  applyPalette(map, key);
  syncSliders(PALETTES[key].light);
}
